import React from 'react';
import { Stepper } from '../../../components/ui/Stepper';
import { Icon } from '../../../components/ui/Icon';
import { useShippingWizard, ShippingSteps } from '../hooks/useShippingWizard';
import { useShippingHandlers } from '../hooks/useShippingHandlers';
import { ShipStepPackage } from './ShipStepPackage';
import { ShipStepRates } from './ShipStepRates';
import { ShipStepDetails } from './ShipStepDetails';
import { ShipStepReview } from './ShipStepReview';

const STEP_LABELS = [
  'Package Details',
  'Service Selection',
  'Package Contents',
  'Summary',
];

export const ShippingOrderWizard: React.FC = () => {
  const wizard = useShippingWizard();
  const handlers = useShippingHandlers(wizard);

  const { loading, initialLoadFailed, errorMessage, currentStep, completedSteps } = wizard;

  const stepperSteps = STEP_LABELS.map((label, i) => ({
    label,
    status: (
      completedSteps.has(i as ShippingSteps)
        ? 'done'
        : i === currentStep
        ? 'active'
        : 'pending'
    ) as 'done' | 'active' | 'pending',
  }));

  if (loading) {
    return (
      <div>
        <div style={{ marginBottom: 24 }}>
          <Stepper steps={stepperSteps} />
        </div>
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '64px 0',
            gap: 16,
          }}
        >
          {initialLoadFailed ? (
            <>
              <Icon name="alertCircle" size={48} style={{ color: 'var(--err)' }} />
              <div style={{ fontWeight: 600, fontSize: 16, color: 'var(--err)' }}>
                Failed to load shipping rates
              </div>
              <div className="muted" style={{ fontSize: 13, textAlign: 'center', maxWidth: 360 }}>
                {errorMessage || 'Unable to fetch shipping rates. Redirecting back…'}
              </div>
              <div
                style={{
                  width: 32,
                  height: 32,
                  border: '3px solid var(--blue)',
                  borderTopColor: 'transparent',
                  borderRadius: '50%',
                  animation: 'spin 0.8s linear infinite',
                }}
              />
              <div className="muted" style={{ fontSize: 13 }}>Redirecting to shipping page…</div>
            </>
          ) : (
            <>
              <div
                style={{
                  width: 48,
                  height: 48,
                  border: '3px solid var(--blue)',
                  borderTopColor: 'transparent',
                  borderRadius: '50%',
                  animation: 'spin 0.8s linear infinite',
                }}
              />
              <div style={{ fontWeight: 500, fontSize: 15 }}>Loading shipping rates…</div>
              <div className="muted" style={{ fontSize: 13 }}>
                Please wait while we prepare your shipment details
              </div>
            </>
          )}
        </div>
      </div>
    );
  }

  return (
    <div>
      <div style={{ marginBottom: 24 }}>
        <Stepper steps={stepperSteps} />
      </div>

      {currentStep === ShippingSteps.PACKAGE_DETAILS && (
        <ShipStepPackage wizard={wizard} handlers={handlers} />
      )}
      {currentStep === ShippingSteps.SERVICE_SELECTION && (
        <ShipStepRates wizard={wizard} handlers={handlers} />
      )}
      {currentStep === ShippingSteps.PACKAGE_CONTENTS && (
        <ShipStepDetails wizard={wizard} handlers={handlers} />
      )}
      {currentStep === ShippingSteps.SUMMARY && (
        <ShipStepReview wizard={wizard} handlers={handlers} />
      )}
    </div>
  );
};
